import * as React from "react";

import { scaleLinear, scaleBand } from "@vx/scale";
import { max } from "d3-array";
import { Group } from "@vx/group";
import { Bar } from "@vx/shape";
import { AxisBottom, AxisLeft } from "@vx/axis";
import { motion } from "framer-motion";
import { styled } from "../../ui/theme";

export type ChartData = {
  name: String;
  total: number;
  delta: number;
  deaths: number;
};

export interface Props {
  width: number;
  data: ChartData[];
  onClick: (index: number) => void;
}

export default function Chart({ width, data, onClick }: Props) {
  const margin = {
    top: 5,
    left: 60,
    right: 10,
    bottom: 40
  };
  const height = margin.top + margin.bottom + data.length * 44;

  // accessors
  const y = (d: ChartData) => d.name;
  const x = (d: ChartData) => d.total;
  const x2 = (d: ChartData) => d.delta;
  const x3 = (d: ChartData) => d.deaths;

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  // // scales
  let maxV = max(data.map(x));
  const xScale = scaleLinear<number>({
    rangeRound: [0, xMax],
    domain: [0, maxV || 1],
    nice: true
  });
  const yScale = scaleBand({
    rangeRound: [yMax, 0],
    domain: data.map(y)
  });

  return (
    <Container>
      <svg width={width} height={height}>
        {data.map((d, i) => {
          const barWidth = xScale(x(d)) as number;
          const barWidth2 = xScale(x2(d)) as number;
          const barWidth3 = xScale(x3(d)) as number;
          const barHeight = yScale.bandwidth() - 2;
          return (
            <Group key={`bar-${i}`} top={margin.top} left={margin.left}>
              <Bar
                x={0}
                y={yScale(y(d))}
                height={barHeight}
                width={Math.max(width - margin.left - margin.right, 0)}
                fill={"#EDF2F7"}
                onClick={() => onClick(i)}
              />
              <motion.rect
                x={0}
                y={yScale(y(d))}
                height={barHeight}
                onClick={() => onClick(i)}
                width={0}
                animate={{
                  width: Math.max(barWidth, 0)
                }}
                transition={{ ease: "easeOut" }}
                rx={4}
                fill={"#6c567b"}
              />
              <motion.rect
                x={0}
                y={yScale(y(d))}
                height={barHeight}
                onClick={() => onClick(i)}
                width={0}
                animate={{
                  width: Math.max(barWidth2, 0)
                }}
                transition={{ ease: "easeOut" }}
                rx={4}
                fill={"#67C98F"}
              />
              <motion.rect
                x={0}
                y={yScale(y(d))}
                height={barHeight}
                onClick={() => onClick(i)}
                width={0}
                animate={{
                  width: Math.max(barWidth3, 0)
                }}
                transition={{ ease: "easeOut" }}
                rx={4}
                fill={"#000000"}
              />
            </Group>
          );
        })}
        <Group key={"axi"} top={margin.top} left={margin.left}>
          <AxisLeft
            scale={yScale}
            stroke={"#333"}
            tickStroke={"#00FF00"}
            tickLabelProps={() => ({
              fill: "#333",
              width: margin.left,
              fontSize: 12,
              textAnchor: "end",
              scaleToFit: false,
              verticalAnchor: "middle"
            })}
            tickFormat={(val: any) => {
              return val;
            }}
          />
          <AxisBottom
            scale={xScale}
            top={yMax}
            stroke={"#333"}
            numTicks={4}
            tickStroke={"#00FF00"}
            tickLabelProps={() => ({
              fill: "#333",
              fontSize: 11,
              textAnchor: "middle"
            })}
          />
        </Group>
      </svg>
    </Container>
  );
}

const Container = styled.div`
  svg {
    rect {
      &:hover {
        fill: #f67281;
      }
    }
  }
`;
